import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import he from "he";
import punycode from "punycode/";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { copyToClipboard } from "@/lib/utils";
import { Copy, RotateCcw, Trash2 } from "lucide-react";

const SAMPLE_HTML = `<div class="main-container" id="widget-1">\n  <h2>Enjoy 247 DevTools Hub &copy; 2026</h2>\n  <p>For inquiries, contact: info@dev-hub.online & "support"</p>\n</div>`;

export function HtmlEntityEncoder() {
  const [input, setInput] = useState(SAMPLE_HTML);
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"encode" | "decode">("encode");
  const [copied, setCopied] = useState(false);

  const process = (val = input, currentMode = mode) => {
    if (currentMode === "encode") {
      setOutput(he.encode(val));
    } else {
      setOutput(he.decode(val));
    }
  };

  useEffect(() => {
    process();
  }, [mode]);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleModeChange = (newMode: "encode" | "decode") => {
    setMode(newMode);
    if (newMode === "encode") {
      setInput(SAMPLE_HTML);
      process(SAMPLE_HTML, "encode");
    } else {
      const encodedSample = `&lt;div class=&quot;main-container&quot; id=&quot;widget-1&quot;&gt;&#10;  &lt;h2&gt;Enjoy 247 DevTools Hub &amp;copy; 2026&lt;/h2&gt;&#10;  &lt;p&gt;For inquiries, contact: info@dev-hub.online &amp; &quot;support&quot;&lt;/p&gt;&#10;&lt;/div&gt;`;
      setInput(encodedSample);
      process(encodedSample, "decode");
    }
  };

  return (
    <ToolPageLayout
      title="HTML Entity Encoder & Decoder Online - Escape HTML"
      description="Safely encode or decode HTML characters into their corresponding HTML entity representations. Prevent HTML injection and format markup strings cleanly."
      h1="HTML Entity Encoder/Decoder"
      category="Encoding / Decoding"
      categoryPath="/encoding-decoding"
      howToUse={[
        "Select your operation: 'Encode HTML' or 'Decode HTML'.",
        "Paste your raw markup or encoded entity parameters in the input editor.",
        "The formatted conversion results will appear in the output console immediately."
      ]}
      faq={[
        { question: "Why do we encode HTML?", answer: "Encoding converts reserved characters like '<' and '>' into safe entities like '&lt;' and '&gt;', preventing browsers from rendering them as active code tags." },
        { question: "Is this tool secure?", answer: "Yes, it executes entirely in your client-side browser memory sandbox." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button 
              onClick={() => handleModeChange("encode")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "encode" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Encode HTML
            </button>
            <button 
              onClick={() => handleModeChange("decode")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "decode" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Decode HTML
            </button>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Input Text</label>
            <Textarea value={input} onChange={e => { setInput(e.target.value); process(e.target.value); }} placeholder="Paste content here..." className="min-h-[250px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Processed Output</label>
            <Textarea value={output} readOnly className="min-h-[250px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_JWT = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiYWRtaW4iOnRydWUsImlhdCI6MTUxNjIzOTAyMn0.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c";

export function JwtDecoder() {
  const [input, setInput] = useState(SAMPLE_JWT);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = (val = input) => {
    try {
      if (!val.trim()) {
        setOutput("");
        return;
      }
      if (!val.includes('.')) throw new Error("Invalid JWT Format (Requires 3 parts separated by dots)");
      const parts = val.split('.');
      const headerBase64 = parts[0].replace(/-/g, '+').replace(/_/g, '/');
      const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      
      const headerJson = JSON.parse(decodeURIComponent(atob(headerBase64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
      const payloadJson = JSON.parse(decodeURIComponent(atob(payloadBase64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
      
      setOutput(JSON.stringify({ header: headerJson, payload: payloadJson }, null, 2));
    } catch (e: any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="JWT Decoder Online - Decode JSON Web Tokens"
      description="Decode and inspect JSON Web Token (JWT) headers and payloads instantly. Verify signature algorithms, subject parameters, or token expiry values."
      h1="JWT Decoder"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Paste your encoded JSON Web Token (JWT) into the left block.",
        "The decoder automatically extracts base64 payloads and formats them as standard JSON on the right.",
        "Copy your parsed metadata payload cleanly."
      ]}
      faq={[
        { question: "What is an online JWT decoder and how does it work?", answer: "An online JWT decoder takes a JSON Web Token string, splits it by the dot ('.') character into its three constituent parts—Header, Payload, and Signature—and decodes each Base64URL-encoded section back into human-readable JSON text or raw bytes directly in your browser." },
        { question: "Is decoding a JSON Web Token (JWT) online secure?", answer: "Yes, this JWT decoder operates 100% client-side. Your token never leaves your browser, ensuring no sensitive session tokens or API keys are transmitted across the internet." },
        { question: "Does this online tool verify the JWT signature?", answer: "This specific tool decodes the Base64 content of a JWT completely offline for debugging. To verify a signature, you need the corresponding cryptographic public key or symmetric HMAC secret." },
        { question: "What is the three-part structure of a JWT?", answer: "A standard JWT consists of three parts separated by dots: the Header (defining the algorithm and token type), the Payload (containing claims and metadata), and the Signature (used to verify the sender's identity and data integrity)." },
        { question: "What are common JWT registered claims like exp, iat, and sub?", answer: "Standard JWT payload claims include 'exp' (Expiration Time), 'iat' (Issued At), 'sub' (Subject/User ID), 'iss' (Issuer), 'aud' (Audience), and 'nbf' (Not Before). These fields help systems manage authorization lifecycles safely." },
        { question: "Can I modify a decoded JWT and encode it back?", answer: "You can decode a token, edit its JSON content, and re-sign it if you possess the secret signature key. However, modifying a token without re-signing it will invalidate the signature, and any secure receiver will reject it." },
        { question: "Why do I see strange characters in my decoded JWT signature?", answer: "The signature is a binary cryptographic fingerprint resulting from algorithms like HMAC-SHA256 or RSA. Because it is binary data encoded as Base64URL, decoding it as standard text or UTF-8 will render garbled characters." },
        { question: "What is the difference between JWS, JWE, and JWT?", answer: "JWT is the general standard for JSON Web Tokens. JWS (JSON Web Signature) represents signed tokens where payload contents are visible but secure from tampering. JWE (JSON Web Encryption) represents tokens where payload contents are fully encrypted and hidden." },
        { question: "What token signature algorithms are supported?", answer: "Common algorithms defined in the header include HS256, HS384, HS512 (symmetric HMAC), and RS256, ES256, PS256 (asymmetric public/private key-pair algorithms)." },
        { question: "Why is Base64URL encoding used instead of standard Base64 for JWT?", answer: "Base64URL replaces characters like '+' with '-' and '/' with '_', and removes padding '='. This ensures that the token string is completely safe to pass in URL query parameters, path variables, and HTTP headers without URL encoding errors." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <Button onClick={() => process()} className="bg-slate-900 hover:bg-slate-800 text-white">Decode JWT Code Now</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_JWT); process(SAMPLE_JWT); }}
              className="text-xs gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output || output.startsWith("Error")}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Payload"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Raw JWT Token</label>
            <Textarea value={input} onChange={e => { setInput(e.target.value); process(e.target.value); }} placeholder="eyJhbGciOiJIUzI1Ni..." className="min-h-[250px] font-mono text-xs break-all" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Decoded Payload Structure</label>
            <Textarea value={output} readOnly className="min-h-[250px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_ASCII_TEXT = "Hi, Dev!";

export function UnicodeAsciiConverter() {
  const [input, setInput] = useState(SAMPLE_ASCII_TEXT);
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"text2ascii" | "ascii2text">("text2ascii");
  const [copied, setCopied] = useState(false);

  const process = (val = input, currentMode = mode) => {
    try {
      if (!val.trim()) {
        setOutput("");
        return;
      }
      if (currentMode === "text2ascii") {
        setOutput(val.split("").map(c => c.charCodeAt(0)).join(" "));
      } else {
        const codes = val.trim().split(/\s+/).map(Number);
        setOutput(String.fromCharCode(...codes));
      }
    } catch (e: any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, [mode]);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleModeChange = (newMode: "text2ascii" | "ascii2text") => {
    setMode(newMode);
    if (newMode === "text2ascii") {
      setInput(SAMPLE_ASCII_TEXT);
      process(SAMPLE_ASCII_TEXT, "text2ascii");
    } else {
      const asciiSample = "72 105 44 32 68 101 118 33";
      setInput(asciiSample);
      process(asciiSample, "ascii2text");
    }
  };

  return (
    <ToolPageLayout
      title="Unicode/ASCII Converter Online - Text CharCode Translator"
      description="Convert standard alphabetic strings into their corresponding decimal ASCII char codes or translate code lists back to readable text values instantly."
      h1="Unicode/ASCII Converter"
      category="Encoding / Decoding"
      categoryPath="/encoding-decoding"
      howToUse={[
        "Toggle your desired direction: 'Text to ASCII' or 'ASCII to Text'.",
        "Input your source characters or space-separated decimal char codes.",
        "Copy your converted text characters or numeric series."
      ]}
      faq={[
        { question: "What is ASCII?", answer: "ASCII (American Standard Code for Information Interchange) represents character lists as numeric values ranging from 0 to 127." },
        { question: "Does this support Unicode characters?", answer: "Yes. It supports extended Unicode characters by mapping them to their corresponding char code values." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button 
              onClick={() => handleModeChange("text2ascii")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "text2ascii" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Text to ASCII
            </button>
            <button 
              onClick={() => handleModeChange("ascii2text")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "ascii2text" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              ASCII to Text
            </button>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Input Data</label>
            <Textarea value={input} onChange={e => { setInput(e.target.value); process(e.target.value); }} placeholder={mode === "text2ascii" ? "Text..." : "ASCII numbers separated by space..."} className="min-h-[250px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Converted Result</label>
            <Textarea value={output} readOnly className="min-h-[250px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_UNICODE_DOMAIN = "münchen-airport.de";

export function PunycodeConverter() {
  const [input, setInput] = useState(SAMPLE_UNICODE_DOMAIN);
  const [output, setOutput] = useState("");
  const [mode, setMode] = useState<"encode" | "decode">("encode");
  const [copied, setCopied] = useState(false);

  const process = (val = input, currentMode = mode) => {
    try {
      if (!val.trim()) {
        setOutput("");
        return;
      }
      if (currentMode === "encode") {
        setOutput(punycode.toASCII(val));
      } else {
        setOutput(punycode.toUnicode(val));
      }
    } catch(e:any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, [mode]);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleModeChange = (newMode: "encode" | "decode") => {
    setMode(newMode);
    if (newMode === "encode") {
      setInput(SAMPLE_UNICODE_DOMAIN);
      process(SAMPLE_UNICODE_DOMAIN, "encode");
    } else {
      const punySample = "xn--mnchen-airport-gsb.de";
      setInput(punySample);
      process(punySample, "decode");
    }
  };

  return (
    <ToolPageLayout
      title="Punycode Converter Online - IDN Domain Translator"
      description="Encode internationalized domain names (IDN) with Unicode characters into DNS-safe ASCII-compatible Punycode format and back instantly."
      h1="Punycode Converter"
      category="Encoding / Decoding"
      categoryPath="/encoding-decoding"
      howToUse={[
        "Toggle your operation direction: 'Unicode to Punycode' or 'Punycode to Unicode'.",
        "Input your domain name or raw punycode sequence.",
        "Retrieve your DNS-safe punycode string instantly."
      ]}
      faq={[
        { question: "What is Punycode?", answer: "Punycode is a special encoding syntax designed for Internationalized Domain Names (IDNs) so that Unicode domains can be resolved cleanly inside the legacy DNS infrastructure." },
        { question: "Does this require an external resolver?", answer: "No, conversions occur completely in-browser." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
            <button 
              onClick={() => handleModeChange("encode")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "encode" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Unicode to Punycode
            </button>
            <button 
              onClick={() => handleModeChange("decode")} 
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${mode === "decode" ? "bg-white text-slate-900 shadow-xs" : "text-slate-500 hover:text-slate-900"}`}
            >
              Punycode to Unicode
            </button>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output || output.startsWith("Error")}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy Output"}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setInput(""); setOutput(""); }}
              className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Clear
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Input Domain / Sequence</label>
            <Textarea value={input} onChange={e => { setInput(e.target.value); process(e.target.value); }} placeholder="example.com..." className="min-h-[250px] font-mono text-xs" />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-slate-500 uppercase">Punycode / Unicode Output</label>
            <Textarea value={output} readOnly className="min-h-[250px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function EncodingTools1() {
  return (
    <ToolPageLayout
      title="Encoding & Conversion Tools Suite"
      description="Explore dynamic browser tools to encode HTML entities, decode JWT tokens, convert text to ASCII, or convert Unicode domains to safe Punycode."
      h1="Encoding & Conversion Suite"
      category="Encoding / Decoding"
      categoryPath="/encoding-decoding"
      howToUse={[
        "Choose any of the specialized tools below.",
        "All calculations run securely inside your browser session."
      ]}
      faq={[
        { question: "Where do these conversions execute?", answer: "100% locally in your client-side browser RAM for total confidentiality." }
      ]}
    >
      <div className="space-y-16">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">HTML Entity Encoder/Decoder</h2><HtmlEntityEncoder /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">JWT Decoder</h2><JwtDecoder /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Unicode/ASCII Converter</h2><UnicodeAsciiConverter /></section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs"><h2 className="text-xl font-bold mb-4">Punycode Converter</h2><PunycodeConverter /></section>
      </div>
    </ToolPageLayout>
  );
}
